import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    forwardRef,
    input,
    output,
    signal,
    viewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Icon } from '../../icon/icon.component';

/**
 * Faixa de valores do slider: gera uma legenda (ícone + rótulo) abaixo do
 * trilho e é destacada enquanto o valor atual pertence a ela.
 *
 * O limite {@link from} é **inclusivo** e o {@link to} é **exclusivo** — assim
 * faixas contíguas (`0–0.3` e `0.3–1`) não disputam o valor de fronteira, que
 * pertence sempre à faixa seguinte. O valor máximo do slider é atendido pela
 * última faixa que começa antes dele.
 */
export interface SliderRange {
    /** Início da faixa (inclusivo). */
    readonly from: number;
    /** Fim da faixa (exclusivo). */
    readonly to: number;
    /** Texto da legenda. */
    readonly label: string;
    /** Classe do ícone de fonte (ex.: `fa-solid fa-palette`). Opcional. */
    readonly icon?: string;
}

/** Formatador do valor exibido, para quando `toFixed` não basta. */
export type SliderValueFormatter = (value: number) => string;

/**
 * Tolerância na comparação do valor com os limites das faixas. O valor já é
 * arredondado à precisão do passo, então serve apenas de margem de segurança.
 */
const RANGE_EPSILON = 1e-9;

/** Gera identificadores únicos quando o consumidor não informa um `id`. */
let nextId = 0;

/**
 * Casas decimais de um número, inclusive em notação científica (`1e-7` → 7).
 * Limitada a 12 casas para manter o `toFixed` dentro do intervalo válido.
 *
 * @param value Número a inspecionar.
 * @returns Quantidade de casas decimais.
 */
function decimalsOf(value: number): number {
    if (!Number.isFinite(value)) {
        return 0;
    }
    const [mantissa, exponent] = String(Math.abs(value)).split('e-');
    const fraction = mantissa.split('.')[1]?.length ?? 0;
    return Math.min(fraction + Number(exponent ?? 0), 12);
}

/**
 * Controle deslizante numérico, integrável a formulários
 * (`ControlValueAccessor`).
 *
 * É um `<input type="range">` nativo estilizado: teclado (setas, Home/End,
 * PageUp/PageDown), arraste por mouse/toque, clique no trilho, `role="slider"`
 * e `aria-valuenow/min/max` vêm do navegador. O componente cuida do visual, do
 * valor formatado (canto superior direito) e das legendas de faixa.
 *
 * O **rótulo textual do campo não é responsabilidade do componente**: envolva-o
 * em `<ai-field [label]="...">`.
 */
@Component({
    selector: 'ai-slider',
    templateUrl: './slider.component.html',
    styleUrls: ['./slider.component.scss'],
    imports: [Icon],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => Slider),
            multi: true,
        },
    ],
})
export class Slider implements ControlValueAccessor {
    /** Valor mínimo do intervalo. */
    readonly min = input<number>(0);

    /** Valor máximo do intervalo. Se menor que {@link min}, é elevado a ele. */
    readonly max = input<number>(100);

    /** Incremento. Valores não positivos desligam o ajuste ao passo. */
    readonly step = input<number>(1);

    /** Faixas exibidas como legendas abaixo do trilho. Vazio oculta a linha. */
    readonly ranges = input<readonly SliderRange[]>([]);

    /** Exibe o valor formatado no canto superior direito. */
    readonly showValue = input<boolean>(true);

    /** Casas decimais exibidas. `null` deriva do {@link step}. */
    readonly decimals = input<number | null>(null);

    /** Formatador do valor exibido. `null` usa `toFixed`. */
    readonly formatValue = input<SliderValueFormatter | null>(null);

    /** Identificador do `<input>` interno (para um `<label for>` externo). */
    readonly id = input<string>(`ai-slider-${++nextId}`);

    /** Rótulo acessível, para quando não há `<label>` associado. */
    readonly ariaLabel = input<string>('');

    /** Emite o novo valor a cada mudança **provocada pelo usuário**. */
    readonly valueChange = output<number>();

    /** `<input type="range">` interno. */
    private readonly inputEl =
        viewChild<ElementRef<HTMLInputElement>>('inputEl');

    /**
     * Valor bruto recebido do formulário, sem normalização. `NaN` representa
     * "sem valor" (ver {@link writeValue}).
     */
    private readonly rawValue = signal<number>(Number.NaN);

    /**
     * Valor atual, sempre dentro de `[min, max]` e alinhado ao passo.
     *
     * Por ser derivado, alterar `min`/`max`/`step` reajusta o valor exibido
     * automaticamente — sem notificar o formulário, como um `writeValue`.
     */
    readonly value = computed<number>(() => this.normalize(this.rawValue()));

    /** Desabilitação vinda do formulário (via {@link setDisabledState}). */
    private readonly cvaDisabled = signal<boolean>(false);

    /** Indica se o componente está desabilitado. */
    readonly disabled = this.cvaDisabled.asReadonly();

    /** Limite superior efetivo — nunca menor que {@link min}. */
    protected readonly upperBound = computed<number>(() =>
        Math.max(this.max(), this.min()),
    );

    /**
     * Casas decimais usadas no arredondamento. O `min` entra na conta porque
     * desloca a grade do passo (`min=0.05, step=0.1` exige 2 casas).
     */
    private readonly precision = computed<number>(() =>
        Math.max(decimalsOf(this.step()), decimalsOf(this.min())),
    );

    /** Casas decimais da exibição. */
    private readonly displayDecimals = computed<number>(
        () => this.decimals() ?? this.precision(),
    );

    /** Valor formatado para exibição. */
    protected readonly displayValue = computed<string>(() => {
        const format = this.formatValue();
        return format
            ? format(this.value())
            : this.value().toFixed(this.displayDecimals());
    });

    /** Proporção preenchida do trilho (0 a 1), consumida pelo SCSS. */
    protected readonly fillRatio = computed<number>(() => {
        const min = this.min();
        const span = this.upperBound() - min;
        return span <= 0 ? 0 : (this.value() - min) / span;
    });

    /**
     * Faixa que contém o valor atual, ou `null` se não houver faixas.
     *
     * Procura `from <= valor < to`; não achando (valor no topo do intervalo ou
     * em uma lacuna entre faixas), devolve a **última** faixa que começa antes
     * do valor.
     */
    protected readonly activeRange = computed<SliderRange | null>(() => {
        const ranges = this.ranges();
        const value = this.value();

        // O epsilon soma nos DOIS lados de propósito: alarga o limite inferior
        // (ruído logo abaixo de `from` ainda pertence à faixa) e estreita o
        // superior (um valor igual a `to` fica de fora, indo para a seguinte).
        const inside = ranges.find(
            (range) =>
                value + RANGE_EPSILON >= range.from &&
                value + RANGE_EPSILON < range.to,
        );
        if (inside) {
            return inside;
        }
        for (let index = ranges.length - 1; index >= 0; index--) {
            if (value + RANGE_EPSILON >= ranges[index].from) {
                return ranges[index];
            }
        }
        return null;
    });

    /**
     * Texto anunciado por leitores de tela no lugar do número cru. `null`
     * quando o número já se descreve sozinho — repetir o `aria-valuenow` em
     * `aria-valuetext` só polui a leitura.
     */
    protected readonly valueText = computed<string | null>(() => {
        const label = this.activeRange()?.label;
        if (label) {
            return `${this.displayValue()}, ${label}`;
        }
        return this.formatValue() ? this.displayValue() : null;
    });

    private onChange: (value: number) => void = () => {};
    private onTouched: () => void = () => {};

    // ─── ControlValueAccessor ────────────────────────────────────────────────

    /**
     * Recebe o valor do formulário.
     *
     * `null`/`undefined`/`NaN` posicionam o polegar no {@link min} — o controle
     * nativo não representa "vazio". O modelo continua nulo até a primeira
     * interação (notificar aqui é proibido pelo contrato do CVA).
     *
     * @param value Valor do formulário.
     */
    writeValue(value: number | null | undefined): void {
        this.rawValue.set(typeof value === 'number' ? value : Number.NaN);
    }

    registerOnChange(fn: (value: number) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Reflete o estado desabilitado do controle de formulário.
     *
     * @param isDisabled `true` quando o controle está desabilitado.
     */
    setDisabledState(isDisabled: boolean): void {
        this.cvaDisabled.set(isDisabled);
    }

    // ─── Interação ───────────────────────────────────────────────────────────

    /**
     * Trata a movimentação do controle nativo — arraste, clique no trilho e
     * teclado chegam todos como evento `input`.
     *
     * Reescreve o campo quando a normalização diverge do que o navegador
     * calculou: a ligação `[value]` não reage se o signal não mudou, e o
     * `<input>` ficaria dessincronizado.
     *
     * @param event Evento `input` do controle nativo.
     */
    protected onInput(event: Event): void {
        if (this.disabled()) {
            return;
        }
        const field =
            event.target instanceof HTMLInputElement
                ? event.target
                : this.inputEl()?.nativeElement;
        if (!field) {
            return;
        }

        const next = this.normalize(Number.parseFloat(field.value));
        if (field.value !== String(next)) {
            field.value = String(next);
        }

        // Valor bruto ainda não finito significa "formulário sem valor": mesmo
        // que o usuário escolha justamente o mínimo, a escolha precisa ser
        // gravada.
        const changed =
            next !== this.value() || !Number.isFinite(this.rawValue());
        this.rawValue.set(next);
        if (!changed) {
            return;
        }
        this.onChange(next);
        this.valueChange.emit(next);
    }

    /** Marca o controle como "tocado" ao sair do campo. */
    protected onBlur(): void {
        this.onTouched();
    }

    // ─── Apoio ───────────────────────────────────────────────────────────────

    /**
     * Limita o valor a `[min, max]` e o alinha ao passo, arredondando na
     * precisão da grade para evitar resíduos de ponto flutuante
     * (`0.1 * 3 = 0.30000000000000004`).
     *
     * Quando o intervalo não é múltiplo exato do passo, o valor **recua um
     * passo** em vez de estourar o máximo — mesmo comportamento do controle
     * nativo, garantindo que a UI e o modelo não divirjam.
     *
     * @param raw Valor bruto.
     * @returns Valor normalizado.
     */
    private normalize(raw: number): number {
        const min = this.min();
        const max = this.upperBound();
        if (!Number.isFinite(raw)) {
            return min;
        }

        const clamped = Math.min(Math.max(raw, min), max);
        const step = this.step();
        if (!Number.isFinite(step) || step <= 0) {
            return clamped;
        }

        const snapped = min + Math.round((clamped - min) / step) * step;
        const bounded = snapped > max ? snapped - step : snapped;
        return Number(Math.max(bounded, min).toFixed(this.precision()));
    }
}
